import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Button, Badge } from '../../components/ui';
import {
  ChevronLeft,
  FileText,
  Download,
  Upload,
  Clock,
  CheckCircle,
  AlertCircle,
  MessageSquare,
  XCircle,
} from 'lucide-react';
import { submissionService } from '../../services/submissions';
import { documentService } from '../../services/documents';

interface Submission {
  id: number;
  program_name: string;
  status: string;
  status_display: string;
  submission_date: string | null;
  last_updated: string;
  submission_contact_name: string;
  submission_contact_email: string;
  submission_contact_phone: string;
  metadata: {
    company_name?: string;
    website?: string;
    company_address?: string;
    [key: string]: any;
  };
  documents?: Array<{
    id: number;
    filename: string;
    file_size: number;
    uploaded_at: string;
    processing_status: string;
  }>;
}

interface Message {
  id: string;
  from: 'vendor' | 'reviewer' | 'system';
  senderName: string;
  content: string;
  timestamp: string;
  type?: 'info' | 'warning' | 'request';
}

const SubmissionDetail = () => {
  const navigate = useNavigate();
  const { submissionId } = useParams<{ submissionId: string }>();
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);

  // Fetch submission data on mount
  useEffect(() => {
    const fetchSubmission = async () => {
      if (!submissionId) return;

      try {
        setLoading(true);
        const data = await submissionService.get(parseInt(submissionId));
        setSubmission(data);

        // Add initial system message based on status
        const initialMessages: Message[] = [
          {
            id: '1',
            from: 'system',
            senderName: 'Overmatch',
            content: getStatusMessage(data.status),
            timestamp: formatDateTime(data.submission_date || data.last_updated),
            type: 'info',
          },
        ];
        setMessages(initialMessages);
      } catch (err) {
        console.error('Failed to fetch submission:', err);
        setError('Failed to load submission details');
      } finally {
        setLoading(false);
      }
    };

    fetchSubmission();
  }, [submissionId]);

  const getStatusMessage = (status: string): string => {
    switch (status) {
      case 'submitted':
      case 'under_review':
        return 'Your submission has been received and is now under review.';
      case 'scored':
        return 'Your submission has been evaluated. The review team will be in touch with next steps.';
      case 'rejected':
        return 'Thank you for your submission. Unfortunately, we will not be moving forward at this time.';
      case 'draft':
        return 'This is a draft submission. Submit it to begin the review process.';
      default:
        return 'Submission status updated.';
    }
  };

  const formatDateTime = (dateStr: string | null): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0] || !submissionId) return;

    const file = e.target.files[0];

    try {
      // Upload the file
      await documentService.uploadDocument(file, parseInt(submissionId));

      // Refresh submission data
      const updated = await submissionService.get(parseInt(submissionId));
      setSubmission(updated);

      // Add system message
      const systemMessage: Message = {
        id: `msg-${Date.now()}`,
        from: 'system',
        senderName: 'Overmatch',
        content: `New file uploaded: ${file.name}`,
        timestamp: new Date().toLocaleString(),
        type: 'info',
      };
      setMessages([...messages, systemMessage]);
    } catch (error) {
      console.error('Failed to upload file:', error);
      alert('Failed to upload file. Please try again.');
    }
  };

  const getStatusBadge = () => {
    if (!submission) return null;

    switch (submission.status) {
      case 'submitted':
      case 'under_review':
        return (
          <Badge variant="info" className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {submission.status_display}
          </Badge>
        );
      case 'scored':
        return (
          <Badge variant="approved" className="flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            {submission.status_display}
          </Badge>
        );
      case 'rejected':
        return (
          <Badge variant="rejected" className="flex items-center gap-1">
            <XCircle className="w-3 h-3" />
            {submission.status_display}
          </Badge>
        );
      case 'draft':
        return (
          <Badge variant="pending" className="flex items-center gap-1">
            <FileText className="w-3 h-3" />
            Draft
          </Badge>
        );
      default:
        return (
          <Badge variant="pending">
            {submission.status_display}
          </Badge>
        );
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading submission...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !submission) {
    return (
      <div className="h-full flex items-center justify-center bg-background">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <p className="text-foreground text-xl font-semibold mb-2">
            {error || 'Submission not found'}
          </p>
          <p className="text-muted-foreground mb-4">
            The submission you're looking for could not be loaded.
          </p>
          <Button onClick={() => navigate('/submit')}>Back to Submissions</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto bg-background">
      <div className="max-w-7xl mx-auto px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/submit')}
            className="flex items-center gap-2 text-muted-foreground hover:text-slate-900 dark:hover:text-slate-200 transition-colors mb-4"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Submissions
          </button>
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-4xl font-bold text-foreground mb-2">
                {submission.program_name}
              </h1>
              <div className="flex items-center gap-3">
                {getStatusBadge()}
                {submission.submission_date && (
                  <span className="text-sm text-muted-foreground">
                    Submitted: {formatDate(submission.submission_date)}
                  </span>
                )}
                <span className="text-sm text-muted-foreground">
                  Last Updated: {formatDate(submission.last_updated)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-8">
          {/* Left Column - Metadata & Files */}
          <div className="col-span-2 space-y-6">
            {/* Metadata */}
            <Card className="p-6">
              <h2 className="text-xl font-bold text-foreground mb-4">
                Submission Details
              </h2>
              <div className="grid grid-cols-2 gap-4">
                {submission.metadata?.company_name && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Company Name</p>
                    <p className="text-sm font-medium text-foreground">
                      {submission.metadata.company_name}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-muted-foreground mb-1">
                    Primary Contact
                  </p>
                  <p className="text-sm font-medium text-foreground">
                    {submission.submission_contact_name || 'Not provided'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Email</p>
                  <p className="text-sm font-medium text-foreground">
                    {submission.submission_contact_email || 'Not provided'}
                  </p>
                </div>
                {submission.submission_contact_phone && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Phone</p>
                    <p className="text-sm font-medium text-foreground">
                      {submission.submission_contact_phone}
                    </p>
                  </div>
                )}
                {submission.metadata?.website && (
                  <div className="col-span-2">
                    <p className="text-sm text-muted-foreground mb-1">Website</p>
                    <a
                      href={submission.metadata.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-primary hover:underline"
                    >
                      {submission.metadata.website}
                    </a>
                  </div>
                )}
                {submission.metadata?.company_address && (
                  <div className="col-span-2">
                    <p className="text-sm text-muted-foreground mb-1">Address</p>
                    <p className="text-sm font-medium text-foreground">
                      {submission.metadata.company_address}
                    </p>
                  </div>
                )}
              </div>
            </Card>

            {/* Files */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-foreground">
                  Submitted Documents
                </h2>
                {submission.status === 'draft' && (
                  <div>
                    <input
                      type="file"
                      id="add-file"
                      className="hidden"
                      onChange={handleFileUpload}
                    />
                    <label htmlFor="add-file" className="cursor-pointer inline-flex items-center justify-center rounded-md text-sm font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-3">
                      <Upload className="w-4 h-4 mr-2" />
                      Add File
                    </label>
                  </div>
                )}
              </div>

              {submission.documents && submission.documents.length > 0 ? (
                <div className="space-y-3">
                  {submission.documents.map((file) => (
                    <div
                      key={file.id}
                      className="flex items-center justify-between p-3 bg-muted rounded-lg border border-border"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <FileText className="w-5 h-5 text-primary" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">
                            {file.filename}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatFileSize(file.file_size)} • Uploaded {formatDate(file.uploaded_at)}
                          </p>
                        </div>
                        {file.processing_status !== 'ready' && (
                          <Badge variant={file.processing_status === 'processing' ? 'info' : 'warning'}>
                            {file.processing_status}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button className="text-muted-foreground hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-slate-300 dark:text-slate-700 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">No documents uploaded yet</p>
                </div>
              )}
            </Card>
          </div>

          {/* Right Column - Messages */}
          <div className="space-y-6">
            <Card className="p-6 flex flex-col h-[calc(var(--app-height)-200px)]">
              <div className="flex items-center gap-2 mb-4">
                <MessageSquare className="w-5 h-5 text-primary" />
                <h2 className="text-xl font-bold text-foreground">
                  Messages & Updates
                </h2>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto space-y-4 mb-4">
                {messages.length > 0 ? (
                  messages.map((message) => (
                    <div
                      key={message.id}
                      className={`p-4 rounded-lg ${
                        message.from === 'vendor'
                          ? 'bg-primary/10 border border-primary/30'
                          : message.from === 'system'
                          ? 'bg-muted border border-border'
                          : 'bg-white dark:bg-slate-900 border border-border'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-foreground">
                            {message.senderName}
                          </p>
                          {message.type === 'request' && (
                            <Badge variant="warning" className="text-xs">
                              Action Required
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 dark:text-muted-foreground">
                          {message.timestamp}
                        </p>
                      </div>
                      <p className="text-sm text-card-foreground">{message.content}</p>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12">
                    <MessageSquare className="w-12 h-12 text-slate-300 dark:text-slate-700 mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">
                      No messages yet. The review team will reach out if they need additional information.
                    </p>
                  </div>
                )}
              </div>

              {/* Message Input - Coming Soon */}
              <div className="border-t border-border pt-4">
                <div className="text-center py-3 bg-muted rounded-lg border border-border">
                  <p className="text-xs text-muted-foreground">
                    Messaging feature coming soon
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmissionDetail;

